import type { CourseHole, GroupRoundEnriched } from '@/lib/db/types';
import type { GameResult } from '@/lib/games/engine/types';
import { scoresMatrixFromDb } from '@/lib/games/engine/compute-round';
import { CLUB_POINT_RULES } from './rules';

export interface RoundPointsAward {
  memberId: string;
  points: number;
  sourceType: string;
  sourceId?: string;
  note: string;
}

export interface ClubPointsLineItem {
  label: string;
  points: number;
  count?: number;
}

interface HoleAchievements {
  birdies: number;
  eagles: number;
  albatrosses: number;
  holeInOnes: number;
}

export function scanHoleAchievements(
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
  playerKey: string,
): HoleAchievements {
  const out: HoleAchievements = { birdies: 0, eagles: 0, albatrosses: 0, holeInOnes: 0 };
  const gross = scores[playerKey] || [];
  holes.forEach((h, i) => {
    const s = gross[i];
    if (s == null || !Number.isFinite(s)) return;
    if (s === 1 && h.par >= 3) {
      out.holeInOnes++;
      return;
    }
    if (s <= h.par - 3) {
      out.albatrosses++;
      return;
    }
    if (s === h.par - 2) {
      out.eagles++;
      return;
    }
    if (s === h.par - 1) {
      out.birdies++;
    }
  });
  return out;
}

/** Fixed Bag Tag club points — independent of game stake settings. */
export function processPointsAfterRound(opts: {
  round: GroupRoundEnriched;
  gameResults: GameResult[];
  holes: CourseHole[];
}): RoundPointsAward[] {
  const { round, gameResults, holes } = opts;
  const awards: RoundPointsAward[] = [];
  const players = round.players || [];
  const matrix = scoresMatrixFromDb(players, (round.scores || []).map((s) => ({
    player_key: s.player_key,
    hole_num: s.hole_num,
    strokes: s.strokes,
  })));

  for (const p of players) {
    if (!p.member_id) continue;
    const memberId = p.member_id;

    awards.push({
      memberId,
      points: CLUB_POINT_RULES.PLAY_ROUND,
      sourceType: 'achievement',
      sourceId: round.id,
      note: 'Play a round',
    });

    const ach = scanHoleAchievements(holes, matrix, p.player_key);
    if (ach.birdies > 0) {
      awards.push({
        memberId,
        points: CLUB_POINT_RULES.BIRDIE * ach.birdies,
        sourceType: 'achievement',
        sourceId: round.id,
        note: ach.birdies === 1 ? 'Birdie' : `Birdies (${ach.birdies})`,
      });
    }
    if (ach.eagles > 0) {
      awards.push({
        memberId,
        points: CLUB_POINT_RULES.EAGLE * ach.eagles,
        sourceType: 'achievement',
        sourceId: round.id,
        note: ach.eagles === 1 ? 'Eagle' : `Eagles (${ach.eagles})`,
      });
    }
    if (ach.albatrosses > 0) {
      awards.push({
        memberId,
        points: CLUB_POINT_RULES.ALBATROSS * ach.albatrosses,
        sourceType: 'achievement',
        sourceId: round.id,
        note: ach.albatrosses === 1 ? 'Albatross' : `Albatrosses (${ach.albatrosses})`,
      });
    }
    if (ach.holeInOnes > 0) {
      awards.push({
        memberId,
        points: CLUB_POINT_RULES.HOLE_IN_ONE * ach.holeInOnes,
        sourceType: 'achievement',
        sourceId: round.id,
        note: ach.holeInOnes === 1 ? 'Hole in One' : `Holes in One (${ach.holeInOnes})`,
      });
    }
  }

  for (const g of gameResults) {
    const winners = g.winners || [];
    for (const wKey of winners) {
      const mp = players.find((x) => x.player_key === wKey);
      if (!mp?.member_id) continue;
      awards.push({
        memberId: mp.member_id,
        points: CLUB_POINT_RULES.MATCH_WIN,
        sourceType: 'achievement',
        sourceId: round.id,
        note: `Match Win · ${g.label}`,
      });
    }
  }

  return awards;
}

export function summarizeClubPointsForMember(
  awards: RoundPointsAward[],
  memberId: string,
): { items: ClubPointsLineItem[]; total: number } {
  const mine = awards.filter((a) => a.memberId === memberId && a.points > 0);
  const items: ClubPointsLineItem[] = mine.map((a) => ({
    label: a.note,
    points: a.points,
  }));
  const total = items.reduce((s, i) => s + i.points, 0);
  return { items, total };
}

export async function applyPointsAwards(awards: RoundPointsAward[]): Promise<void> {
  const { db } = await import('@/lib/db');
  const batch = awards
    .filter((a) => a.points !== 0)
    .map((a) => ({
      member_id: a.memberId,
      points: a.points,
      reason: a.note,
      source_type: a.sourceType,
      source_id: a.sourceId,
    }));
  await db.points.awardBatch(batch);
}
